import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { usePageTracking } from './hooks/usePageTracking';
import { useTranslation } from 'react-i18next';
import FeatureCarousel from './components/FeatureCarousel';
import FeatureSections from './components/FeatureSections';
import Reviews from './components/Reviews';
import WhoBenefits from './components/WhoBenefits';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import Dashboard from './components/Dashboard';
import PricingPage from './components/Pricing';
import FeaturesPage from './components/Features';
import ContactPage from './components/Contact';
import Navigation from './components/Navigation';
import DarkFeatureDetail from './components/DarkFeatureDetail';
import CTABanner from './components/CTABanner';
import QRPage from './components/QRPage';
import QRDemoPage from './components/QRDemoPage';
import placeholder from './images/placeholder.png';
import { ReactComponent as AppStoreBadge } from './images/badge-appstore.svg';
import { ReactComponent as GooglePlayBadge } from './images/badge-googleplay.svg';
import { ReactComponent as MacAppStoreBadge } from './images/badge-macappstore.svg';
import ProcessLogs from './components/ProcessLogs';
import FeedbackDashboard from './components/FeedbackDashboard';
import PasswordProtected from './components/PasswordProtected';
import Subscriptions from './components/Subscriptions';
import DailyUsage from './components/DailyUsage';
import PromoCodes from './components/PromoCodes';
import LocaleRouter from './components/LocaleRouter';
import StatTooltip from './components/StatTooltip';
import DownloadRedirect from './components/DownloadRedirect';
import LocalizedDownloadRedirect from './components/LocalizedDownloadRedirect';
import RedeemRedirect from './components/RedeemRedirect';
import { getStoreUrl, getIOSUrl, getAndroidUrl, getMacUrl } from './utils/storeUrls';



// Home Page
const HomePage = () => {
  const { t, i18n } = useTranslation();
  const storeUrl = getStoreUrl(i18n.language);

  return (
    <div>
      {/* Hero */}
      <section className="pt-24 pb-0 text-center bg-white px-4 dark:bg-slate-900 overflow-hidden">
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Notely AI",
            "applicationCategory": "ProductivityApplication",
            "operatingSystem": "iOS, Android, macOS",
            "description": "AI-powered note-taking and voice recording app with instant transcription, smart summaries, custom categorization, and cross-device synchronization.",
          })}
        </script>

        <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight animate-slide-up opacity-0 dark:text-white" style={{ animationDelay: '0.2s' }}>
          {t('home.hero.titlePart1', 'Never take notes')}{' '}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-primary to-violet-400 bg-clip-text text-transparent">
              {t('home.hero.titleHighlight', 'manually')}
            </span>
            <span
              className="absolute -bottom-1 left-0 w-full h-[3px] rounded-full bg-gradient-to-r from-primary to-violet-400"
              aria-hidden="true"
            />
          </span>{' '}
          {t('home.hero.titlePart2', 'again')}
        </h1>
        <p className="text-xl md:text-2xl text-gray-500 mb-10 max-w-2xl mx-auto animate-slide-up opacity-0 dark:text-gray-400" style={{ animationDelay: '0.3s' }}>
          {t('home.hero.subtitle')}
        </p>
        <div className="animate-slide-up opacity-0" style={{ animationDelay: '0.4s' }}>
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-primary/90 shadow-lg inline-block"
          >
            {t('home.hero.downloadFree')}
          </a>
          <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">{t('home.hero.userStats')}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <a
              href={getIOSUrl(i18n.language)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 dark:text-white opacity-50 hover:opacity-80 transition-opacity"
            >
              <AppStoreBadge aria-label="Download on the App Store" />
            </a>
            <a
              href={getAndroidUrl(i18n.language)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 dark:text-white opacity-50 hover:opacity-80 transition-opacity"
            >
              <GooglePlayBadge aria-label="Get it on Google Play" />
            </a>
            <a
              href={getMacUrl(i18n.language)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 dark:text-white opacity-50 hover:opacity-80 transition-opacity"
            >
              <MacAppStoreBadge aria-label="Download on the Mac App Store" />
            </a>
          </div>
        </div>
      </section>

      {/* App screenshot */}
      <div className="bg-gradient-to-b from-white to-gray-50 pt-16 animate-slide-up opacity-0 dark:from-slate-900 dark:to-slate-800" style={{ animationDelay: '0.6s' }}>
        <div className="max-w-5xl mx-auto px-4">
          <img
            src={placeholder}
            className="w-full rounded-3xl shadow-2xl dark:shadow-none"
            alt={t('home.hero.imageAlt')}
          />
        </div>
      </div>

      {/* Stats */}
      <section className="py-20 bg-white dark:bg-slate-900">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 animate-slide-up opacity-0 dark:text-white" style={{ animationDelay: '0.8s' }}>
              {t('home.trust.title')}
            </h2>
            <p className="text-xl text-gray-500 animate-slide-up opacity-0 dark:text-gray-400" style={{ animationDelay: '0.9s' }}>
              {t('home.trust.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 animate-slide-up opacity-0" style={{ animationDelay: '1s' }}>
            <StatTooltip
              content={{
                title: t('home.trust.popups.minutes.title'),
                description: t('home.trust.popups.minutes.description'),
                details: Array.isArray(t('home.trust.popups.minutes.details', { returnObjects: true }))
                  ? t('home.trust.popups.minutes.details', { returnObjects: true }) as string[]
                  : [],
              }}
              className="text-center"
            >
              <div className="text-primary text-4xl md:text-5xl font-bold mb-2">50K+</div>
              <div className="text-gray-500 text-sm dark:text-gray-400">{t('home.trust.stats.minutes')}</div>
            </StatTooltip>

            <StatTooltip
              content={{
                title: t('home.trust.popups.downloads.title'),
                description: t('home.trust.popups.downloads.description'),
                details: Array.isArray(t('home.trust.popups.downloads.details', { returnObjects: true }))
                  ? t('home.trust.popups.downloads.details', { returnObjects: true }) as string[]
                  : [],
              }}
              className="text-center"
            >
              <div className="text-primary text-4xl md:text-5xl font-bold mb-2">10K+</div>
              <div className="text-gray-500 text-sm dark:text-gray-400">{t('home.trust.stats.downloads')}</div>
            </StatTooltip>

            <StatTooltip
              content={{
                title: t('home.trust.popups.languages.title'),
                description: t('home.trust.popups.languages.description'),
                details: Array.isArray(t('home.trust.popups.languages.details', { returnObjects: true }))
                  ? t('home.trust.popups.languages.details', { returnObjects: true }) as string[]
                  : [],
              }}
              className="text-center"
            >
              <div className="text-primary text-4xl md:text-5xl font-bold mb-2">90+</div>
              <div className="text-gray-500 text-sm dark:text-gray-400">{t('home.trust.stats.languages')}</div>
            </StatTooltip>

            <StatTooltip
              content={{
                title: t('home.trust.popups.rating.title'),
                description: t('home.trust.popups.rating.description'),
                details: Array.isArray(t('home.trust.popups.rating.details', { returnObjects: true }))
                  ? t('home.trust.popups.rating.details', { returnObjects: true }) as string[]
                  : [],
              }}
              className="text-center"
            >
              <div className="text-primary text-4xl md:text-5xl font-bold mb-2">4.9★</div>
              <div className="text-gray-500 text-sm dark:text-gray-400">{t('home.trust.stats.rating')}</div>
            </StatTooltip>
          </div>
        </div>
      </section>

      <DarkFeatureDetail />
      <FeatureCarousel />
      <WhoBenefits />
      <FeatureSections />
      <Reviews />
      <CTABanner />
      <FAQSection />
      <Footer />
    </div>
  );
};

// Terms Page
const TermsPage = () => (
  <TermsOfUse />
);

// Privacy Page
const PrivacyPage = () => (
  <PrivacyPolicy />
);

// English-only wrapper components for non-localized routes
const EnglishTermsPage = () => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);
  
  return <TermsOfUse />;
};

const EnglishPrivacyPage = () => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);
  
  return <PrivacyPolicy />;
};



// App Content Component (needs to be inside BrowserRouter to use hooks)
const AppContent = () => {
  usePageTracking();
  
  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-slate-900 dark:text-white">
      <Navigation />
      <Routes>
          {/* Default route redirects to language-specific route */}
          <Route path="/" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          
          {/* Locale-based routes */}
          <Route path="/:locale" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          <Route path="/:locale/" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          <Route path="/:locale/pricing" element={<LocaleRouter><PricingPage /></LocaleRouter>} />
          <Route path="/:locale/features" element={<LocaleRouter><FeaturesPage /></LocaleRouter>} />
          <Route path="/:locale/contact" element={<LocaleRouter><ContactPage /></LocaleRouter>} />
          <Route path="/:locale/terms" element={<LocaleRouter><TermsPage /></LocaleRouter>} />
          <Route path="/:locale/privacy" element={<LocaleRouter><PrivacyPage /></LocaleRouter>} />
          <Route path="/:locale/download" element={<LocalizedDownloadRedirect />} />
          
          {/* Non-localized routes (admin/dashboard) */}
          <Route path="/dashboard-metrics" element={<PasswordProtected><Dashboard /></PasswordProtected>} />
          <Route path="/dashboard-process-logs" element={<PasswordProtected><ProcessLogs /></PasswordProtected>} />
          <Route path="/dashboard-logs-feedback" element={<PasswordProtected><FeedbackDashboard /></PasswordProtected>} />
          <Route path="/dashboard-subscriptions" element={<PasswordProtected><Subscriptions /></PasswordProtected>} />
          <Route path="/dashboard-daily-usage" element={<PasswordProtected><DailyUsage /></PasswordProtected>} />
          <Route path="/dashboard-promo-codes" element={<PasswordProtected><PromoCodes /></PasswordProtected>} />
          <Route path="/qr" element={<QRPage />} />
          <Route path="/qrcode" element={<QRDemoPage />} />
          <Route path="/:locale/qr" element={<LocaleRouter><QRPage /></LocaleRouter>} />
          <Route path="/:locale/qrcode" element={<LocaleRouter><QRDemoPage /></LocaleRouter>} />
          <Route path="/download" element={<DownloadRedirect />} />
          <Route path="/code/:code" element={<RedeemRedirect />} />
          
          {/* Non-localized legal pages (default to English) */}
          <Route path="/privacy" element={<EnglishPrivacyPage />} />
          <Route path="/terms" element={<EnglishTermsPage />} />
        </Routes>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;